import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import PaginatedLoader from "./component/PaginatedLoader";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Side } from "@/components/ui/customChervonIcon";
import {
  Popover,
  PopoverContent,
  PopoverTriggerButton,
} from "@/components/ui/popover";
import useDebounce from "@/hooks/useDebounce";
import { cn } from "@/lib/utils";

export interface Option {
  value: string;
  label: string;
  disabled?: boolean;
  description?: string;
  icon?: React.ReactNode;
}

export interface RTKQueryHookResult<T> {
  data?: GeneralResponse<T[]>;
  error?: FetchBaseQueryError | SerializedError;
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
}

export interface RTKQueryHook<T, P> {
  (params: P, options?: UseQueryOptions): RTKQueryHookResult<T>;
}

export interface PaginationState {
  offset: number;
  limit: number;
  query?: string;
}

export interface FilterConfig {
  offsetKey?: string;
  limitKey?: string;
  queryKey?: string;
  /* All the queries of pagination and search will be send to this key,ie: builder.query<
   T,
    { user: string; status: string; filter?: string }
  >
  * If not provided, it will default to "filter".
  * If you want to use a different key for query params, you can set it here.
  */
  queryParamsParamKey?: string;
  customBuilder?: (params: PaginationState) => string;
}

// Render configuration types
export type RenderConfig<T> = {
  /** Key for the option value */
  valueKey: keyof T;
  /** Key for the option label */
  labelKey?: keyof T;
  /** Custom label renderer function (takes priority over labelKey) */
  labelRenderer?: (option: T) => React.ReactNode;
};

type PaginatedQueryResult<T, P> = ReturnType<RTKQueryHook<T, P>> & {
  paginationState: PaginationState;
  setPaginationState: React.Dispatch<React.SetStateAction<PaginationState>>;
  setOffset: (offset: number) => void;
  setLimit: (limit: number) => void;
  setQuery: (query?: string) => void;
  resetPagination: () => void;
  loadMore: () => void;
  hasMore: boolean;
};
const usePaginatedQuery = <T, P = void>(
  useQuery: RTKQueryHook<T, P>,
  baseParams?: P extends void ? never : Omit<P, "filter">,
  config: FilterConfig = {},
  skip: boolean = false,
): PaginatedQueryResult<T, P> => {
  const [paginationState, setPaginationState] = useState<PaginationState>({
    offset: 0,
    limit: 10,
  });

  const {
    offsetKey = "offset",
    limitKey = "limit",
    queryKey = "search",
    queryParamsParamKey = "filter",
    customBuilder,
  } = useMemo(() => config, [config]);

  // Build filter string
  const filterString = useMemo(() => {
    if (customBuilder) {
      return customBuilder(paginationState);
    }

    const params = new URLSearchParams();
    params.append(offsetKey, paginationState.offset.toString());
    params.append(limitKey, paginationState.limit.toString());
    if (paginationState.query) {
      params.append(queryKey, paginationState.query);
    }

    const hasBaseParams =
      baseParams && Object.keys(baseParams as object).length > 0;
    const prefix = hasBaseParams ? "&" : "?";

    return `${prefix}${params.toString()}`;
  }, [
    paginationState,
    offsetKey,
    limitKey,
    queryKey,
    customBuilder,
    baseParams,
  ]);

  const queryParams = useMemo(
    () =>
      ({
        ...baseParams,
        [queryParamsParamKey]: filterString,
      }) as P,
    [baseParams, filterString, queryParamsParamKey],
  );

  const getQueries = useMemo((): P => {
    if (baseParams) {
      return queryParams as P;
    }
    if (typeof baseParams === "string") {
      return filterString as P; //When the query accept only a string as parameter eg:  builder.query<T, string >
    }

    return {
      [queryParamsParamKey]: filterString, // When the query accepts an object with a filter key eg: builder.query<T, { filter: string }>
    } as P;
  }, [baseParams, queryParams, filterString, queryParamsParamKey]);

  const queryResult = useQuery(getQueries, {
    skip,
  });

  const setOffset = useCallback((offset: number) => {
    setPaginationState((prev) => ({ ...prev, offset }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setPaginationState((prev) => ({ ...prev, limit, offset: 0 }));
  }, []);

  const setQuery = useCallback((query?: string) => {
    setPaginationState((prev) => ({ ...prev, query, offset: 0 }));
  }, []);

  const resetPagination = useCallback(() => {
    setPaginationState((prev) => ({ ...prev, offset: 0 }));
  }, []);

  const loadMore = useCallback(() => {
    setPaginationState((prev) => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  }, []);

  return {
    ...queryResult,
    paginationState,
    setPaginationState,
    setOffset,
    setLimit,
    setQuery,
    resetPagination,
    loadMore,
    hasMore: queryResult.data?.pagination?.totalResult
      ? paginationState.offset + (queryResult.data?.data?.length || 0) <
        queryResult.data.pagination.totalResult
      : (queryResult.data?.data?.length || 0) >= paginationState.limit,
  };
};

export interface AsyncSingleSelectProps<T, P = void> {
  /** RTK Query hook to fetch options */
  useQuery: RTKQueryHook<T, P>;
  /** Base query parameters (user, status, etc.) */
  baseParams?: P extends void ? never : Omit<P, "filter">;
  /** Filter configuration */
  filterConfig?: FilterConfig;
  /** Preload all data ahead of time */
  preload?: boolean;
  /** Function to filter options (only used in preload mode) */
  filterFn?: (option: T, query: string) => boolean;
  /** Render configuration for keys and custom renderers */
  renderConfig: RenderConfig<T>;
  /** Default label to show when value is set but option is not yet fetched */
  defaultLabel?: string;
  /** Custom not found message */
  notFound?: React.ReactNode;
  /** Custom loading skeleton */
  loadingSkeleton?: React.ReactNode;
  /** Currently selected value */
  value: string;
  /**
   * Callback function invoked when the selected value changes.
   * @template T - The type of the associated data object (optional)
   * @param {string} value - The new selected value
   * @param {T} [obj] - Optional associated data object corresponding to the selected value
   */
  onChange: (value: string, obj?: T) => void;
  /** Label for the select field */
  label: string;
  /** Placeholder text when no selection */
  placeholder?: string;
  /** Disable the entire select */
  disabled?: boolean;
  /** Custom width for the popover */
  width?: string | number;
  /** Custom class names */
  className?: string;
  /** Custom trigger button class names */
  triggerClassName?: string;
  /** Custom no results message */
  noResultsMessage?: string;
  /** Allow clearing the selection */
  clearable?: boolean;
  /** Limit for pagination (default: 20) */
  limit?: number;
  /** Threshold for loading more items (pixels from bottom) */
  loadMoreThreshold?: number;
  /** Side to display the popover content */
  side?: Side;
}

export const AsyncSingleSelect = <T, P = void>({
  useQuery,
  baseParams,
  filterConfig,
  preload,
  filterFn,
  renderConfig,
  defaultLabel,
  notFound,
  loadingSkeleton,
  label,
  placeholder = "Select...",
  value,
  onChange,
  disabled = false,
  className,
  triggerClassName,
  noResultsMessage,
  clearable = true,
  limit = 10,
  loadMoreThreshold = 100,
  side,
}: AsyncSingleSelectProps<T, P>): JSX.Element => {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [allOptions, setAllOptions] = useState<T[]>([]);
  const [displayOptions, setDisplayOptions] = useState<T[]>([]);
  const [selectedValue, setSelectedValue] = useState(value);
  const [selectedOption, setSelectedOption] = useState<T | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Cache to persist selected options that might not be in current results
  const [selectedOptionsCache, setSelectedOptionsCache] = useState<
    Map<string, T>
  >(new Map());

  const prevOptionsRef = useRef<T[]>([]);
  const prevOffsetRef = useRef<number>(0);

  const debouncedSearchTerm = useDebounce(searchTerm, preload ? 0 : 300);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const skipQuery = !open && !preload;

  const memoizedFilterConfig = useMemo(() => {
    return {
      offsetKey: filterConfig?.offsetKey || "offset",
      limitKey: filterConfig?.limitKey || "limit",
      queryKey: filterConfig?.queryKey || "search",
      queryParamsParamKey: filterConfig?.queryParamsParamKey || "filter",
      customBuilder: filterConfig?.customBuilder,
    };
  }, [filterConfig]);

  const {
    data,
    error,
    isLoading,
    isFetching,
    paginationState,
    setLimit,
    setQuery,
    loadMore,
    hasMore,
  } = usePaginatedQuery(useQuery, baseParams, memoizedFilterConfig, skipQuery);

  const getOptionValue = useMemo(() => {
    return (option: T): string => {
      const value = option[renderConfig.valueKey];

      return String(value || "");
    };
  }, [renderConfig.valueKey]);

  const renderOption = useCallback(
    (option: T): React.ReactNode => {
      if (renderConfig.labelRenderer) {
        return renderConfig.labelRenderer(option);
      }

      if (renderConfig.labelKey) {
        const label = option[renderConfig.labelKey];

        return <div>{String(label || "")}</div>;
      }

      const value = option[renderConfig.valueKey];

      return <div>{String(value || "")}</div>;
    },
    [renderConfig],
  );

  const getDisplayValue = useCallback(
    (option: T): React.ReactNode => {
      const displayKey = renderConfig.labelKey || renderConfig.valueKey;
      const displayValue = option[displayKey];

      return String(displayValue || "");
    },
    [renderConfig],
  );

  useEffect(() => {
    setMounted(true);
    setSelectedValue(value);
  }, [value]);

  useEffect(() => {
    if (paginationState.limit !== limit) {
      setLimit(limit);
    }
  }, [limit, paginationState.limit, setLimit]);

  useEffect(() => {
    if (mounted && !skipQuery) {
      setQuery(debouncedSearchTerm || undefined);
      setIsLoadingMore(false);
    }
  }, [debouncedSearchTerm, setQuery, mounted, skipQuery]);

  const options = data?.data || [];

  useEffect(() => {
    if (!skipQuery && options.length > 0) {
      const optionsChanged =
        options.length !== prevOptionsRef.current.length ||
        options.some(
          (opt, index) =>
            getOptionValue(opt) !==
            getOptionValue(prevOptionsRef.current[index] || ({} as T)),
        );

      const offsetChanged = paginationState.offset !== prevOffsetRef.current;

      if (optionsChanged || offsetChanged) {
        setSelectedOptionsCache((prevCache) => {
          const newCache = new Map(prevCache);
          let cacheUpdated = false;

          options.forEach((option) => {
            const optionValue = getOptionValue(option);
            if (
              optionValue &&
              (optionValue === value || prevCache.has(optionValue)) &&
              !newCache.has(optionValue)
            ) {
              newCache.set(optionValue, option);
              cacheUpdated = true;
            }
          });

          return cacheUpdated ? newCache : prevCache;
        });

        if (paginationState.offset === 0) {
          setAllOptions(options);
        } else {
          setAllOptions((prev) => {
            const newOptions = options.filter(
              (newOpt) =>
                !prev.some(
                  (existingOpt) =>
                    getOptionValue(existingOpt) === getOptionValue(newOpt),
                ),
            );

            return [...prev, ...newOptions];
          });
        }

        prevOptionsRef.current = options;
        prevOffsetRef.current = paginationState.offset;
      }

      setIsLoadingMore(false);
    } else if (!skipQuery && paginationState.offset === 0) {
      setAllOptions([]);
      prevOptionsRef.current = [];
      prevOffsetRef.current = 0;
    }
  }, [options, paginationState.offset, getOptionValue, skipQuery, value]);

  useEffect(() => {
    let filteredOptions = allOptions;

    if (preload && debouncedSearchTerm && filterFn) {
      filteredOptions = allOptions.filter((option) =>
        filterFn(option, debouncedSearchTerm),
      );
    }

    const optionValues = new Set(filteredOptions.map(getOptionValue));
    const additionalOptions: T[] = [];

    selectedOptionsCache.forEach((cachedOption, cachedValue) => {
      if (!optionValues.has(cachedValue)) {
        if (preload && debouncedSearchTerm && filterFn) {
          if (filterFn(cachedOption, debouncedSearchTerm)) {
            additionalOptions.push(cachedOption);
          }
        } else if (!debouncedSearchTerm || preload) {
          additionalOptions.push(cachedOption);
        }
      }
    });

    setDisplayOptions([...filteredOptions, ...additionalOptions]);
  }, [
    preload,
    allOptions,
    debouncedSearchTerm,
    filterFn,
    selectedOptionsCache,
    getOptionValue,
  ]);

  useEffect(() => {
    if (value) {
      // First try to find in display options
      let option = displayOptions.find((opt) => getOptionValue(opt) === value);

      // If not found, try to find in cache
      if (!option && selectedOptionsCache.has(value)) {
        option = selectedOptionsCache.get(value) || undefined;
      }

      setSelectedOption(option || null);
    } else {
      setSelectedOption(null);
    }
  }, [value, displayOptions, getOptionValue, selectedOptionsCache]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (preload || !hasMore || isFetching || isLoading || isLoadingMore) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const isNearBottom =
        scrollHeight - scrollTop - clientHeight < loadMoreThreshold;

      if (isNearBottom) {
        setIsLoadingMore(true);
        loadMore();
      }
    },
    [
      preload,
      hasMore,
      isFetching,
      isLoading,
      isLoadingMore,
      loadMoreThreshold,
      loadMore,
    ],
  );

  const handleSelect = useCallback(
    (currentValue: string) => {
      const newValue =
        clearable && currentValue === selectedValue ? "" : currentValue;
      setSelectedValue(newValue);

      // Find and cache the selected option
      const option = displayOptions.find(
        (opt) => getOptionValue(opt) === newValue,
      );

      if (option && newValue) {
        const newCache = new Map(selectedOptionsCache);
        newCache.set(newValue, option);
        setSelectedOptionsCache(newCache);
      }

      setSelectedOption(option || null);
      onChange(newValue, option);
      setOpen(false);
      setSearchTerm("");
    },
    [
      selectedValue,
      onChange,
      clearable,
      displayOptions,
      getOptionValue,
      selectedOptionsCache,
    ],
  );

  const handleOpenChange = useCallback((newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSearchTerm("");
    }
  }, []);

  const triggerDisplayValue = useMemo(() => {
    if (selectedOption) {
      return getDisplayValue(selectedOption);
    }

    if (value) {
      const cachedOption = selectedOptionsCache.get(value);
      if (cachedOption) {
        return getDisplayValue(cachedOption);
      }

      return defaultLabel || value;
    }

    return placeholder;
  }, [
    selectedOption,
    value,
    defaultLabel,
    getDisplayValue,
    placeholder,
    selectedOptionsCache,
  ]);

  const loadingStates = useMemo(() => {
    const isFirstLoad = paginationState.offset === 0;
    const hasSearchQuery = searchTerm.trim().length > 0;

    return {
      showSearchLoader: hasSearchQuery && isFetching && !isLoadingMore,
      showMainSkeleton:
        (isLoading || (isFetching && isFirstLoad && !isLoadingMore)) &&
        !preload &&
        !searchTerm,
      showLoadMoreIndicator:
        isFetching && isLoadingMore && hasMore && displayOptions.length > 0,
    };
  }, [
    paginationState.offset,
    searchTerm,
    isFetching,
    isLoading,
    isLoadingMore,
    hasMore,
    displayOptions.length,
    preload,
  ]);

  const hasValidTriggerValue =
    selectedOption ||
    (value && selectedOptionsCache.has(value)) ||
    defaultLabel;

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={true}>
      <PopoverTriggerButton
        role='combobox'
        className={cn(
          "justify-between",
          disabled && "opacity-50 cursor-not-allowed",
          triggerClassName,
          !hasValidTriggerValue && "text-muted-foreground/35",
        )}
        disabled={disabled}
        side={side}
      >
        {triggerDisplayValue}
      </PopoverTriggerButton>
      <PopoverContent
        className={cn(
          "p-2 popover-content-width-full border border-input rounded-[0.5rem] shadow-select min-w-fit",
          className,
        )}
        align='start'
        portal={false}
        side={side}
      >
        <Command shouldFilter={false}>
          <div className='relative border-b w-full'>
            <CommandInput
              placeholder={`Search ${label.toLowerCase()}...`}
              value={searchTerm}
              onValueChange={setSearchTerm}
            />
            {loadingStates.showSearchLoader && (
              <div className='absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center'>
                <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
              </div>
            )}
          </div>
          <CommandList
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className='max-h-60 overflow-y-scroll'
          >
            {error && (
              <div className='p-4 text-destructive text-center'>
                Failed to fetch options
              </div>
            )}

            {loadingStates.showMainSkeleton &&
              (loadingSkeleton || <PaginatedLoader />)}

            {!isLoading &&
              !error &&
              displayOptions.length === 0 &&
              !loadingStates.showMainSkeleton &&
              (notFound || (
                <CommandEmpty>
                  {noResultsMessage ?? `No ${label.toLowerCase()} found.`}
                </CommandEmpty>
              ))}

            {displayOptions.length > 0 && (
              <CommandGroup>
                {displayOptions.map((option) => {
                  const optionValue = getOptionValue(option);

                  return (
                    <CommandItem
                      key={optionValue}
                      value={optionValue}
                      onSelect={handleSelect}
                      className='line-clamp-2 truncate'
                    >
                      {renderOption(option)}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {loadingStates.showLoadMoreIndicator && (
              <div className='p-3 text-center border-t bg-muted/5'>
                <div className='flex items-center justify-center gap-2'>
                  <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
                  <p className='text-sm text-muted-foreground'>
                    Loading more...
                  </p>
                </div>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
